import { api } from "./axios";

// Playlist placements, served by the Cadence backend rather than fetched from SoundCharts here.
//
// The backend walks SoundCharts' page.next to the end, deduplicates across the whole set, and only
// then pages the result. That ordering is why `meta.totalPlaylists` can be trusted above the table
// and `page.total` cannot: SoundCharts counts raw rows, and several platforms return the same
// playlist once per market.

export interface PlaylistQuery {
  /** editorial | algorithmic | algotorial | curators_listeners (SoundCloud's equivalent of editorial) */
  type?: string;
  /** 1 for current placements, 0 for the historical view */
  currentOnly?: 0 | 1;
  /**
   * Omit for all countries. Where it is applied depends on the platform (AD-310): at
   * SoundCharts on Apple Music and Amazon, on the backend on Spotify and Audiomack, where
   * "none" asks for the playlists with no country code.
   */
  countryCode?: string;
  /** Filters the whole set on the backend before it is paged, so the counts describe the matches. */
  search?: string;
  page?: number;
  /** Rows per page, up to 2000. The table asks for tens; the export asks for a whole range. */
  limit?: number;
  /**
   * Leave out chart and country variant playlists ("Top 100: Australia", "Top 100: Singapore").
   * The backend decides which they are when it stores them. The table and the export both pass
   * it, so the export matches what is on screen.
   */
  hideGeoVariants?: boolean;
  /** Only the playlists of one family ("top 100"), to see every variant of one chart. */
  playlistFamily?: string;
  /**
   * Set on the read straight after a refresh. That URL is identical to the one just fetched, so
   * without a unique parameter the browser can answer it from its own cache and show the rows
   * from before the refresh.
   */
  bustBrowserCache?: boolean;
}

export type PlaylistSyncStatus = "never_synced" | "running" | "complete" | "capped" | "failed";

export interface PlaylistMeta {
  /**
   * Count to show above the table. Deduplicated, and on stored data it leaves out stale
   * placements, which are listed but not counted — so page through `pagination.totalItems`,
   * not this.
   */
  totalPlaylists: number;
  /** True when the backend's page cap stopped the fetch early — tell the user results are partial. */
  capped: boolean;
  /** True when a SoundCharts request failed mid-walk; the set below is incomplete. */
  failed: boolean;
  /** Raw pre-deduplication count from SoundCharts. Diagnostic only — never render it as the total. */
  rawTotal: number;
  latestCrawlDate: string | null;
  pagesFetched: number;
  /** The market the set is scoped to; null means every market. */
  countryCode?: string | null;
  /** "db" when served from stored data, "live" when fetched from SoundCharts on the spot. */
  source?: "db" | "live";
  /** Chart and country variant playlists left out because hideGeoVariants was on. */
  hiddenGeoVariants?: number;
  /** How the country filter works on this platform: api, local or hidden (AD-310). */
  countryFilterMode?: "api" | "local" | "hidden";
  /**
   * Sent whenever a filter is on: what is shown against what is available, and what each
   * filter left out. noCountryData counts playlists dropped only for having no country code.
   * Stale placements are left out of every figure, as they are of totalPlaylists.
   */
  filterCounts?: {
    shown: number;
    available: number;
    noCountryData: number;
    hiddenGeoVariants: number;
  };
  // The fields below are sent on stored data only.
  /** SoundCharts' crawl behind the newest complete sync. Shown as "Data as of". */
  dataAsOf?: string | null;
  /** Stale placements in the set: listed, but not in totalPlaylists. */
  staleCount?: number;
  /** Days without confirmation after which a placement counts as stale. */
  staleAfterDays?: number;
  syncStatus?: PlaylistSyncStatus;
  /** False when the country asked for is outside what the sync stored. */
  countryCovered?: boolean | null;
}

/**
 * What stored rows carry beyond SoundCharts' own fields: which dates are Cadence's own
 * observations rather than SoundCharts', and whether a current placement has gone stale.
 */
export interface PlaylistRowHonesty {
  /** The entry date is the day Cadence first saw the placement, not when it began. */
  entryDateIsObserved?: boolean;
  /** The exit date is the last day Cadence saw the placement, not SoundCharts' date. */
  exitIsObserved?: boolean;
  /** Still listed as current, but not confirmed by SoundCharts recently. */
  isStale?: boolean;
  lastConfirmedAt?: string | null;
}

export interface PlaylistPagination {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface PlaylistResponse {
  success: boolean;
  data: any[];
  meta: PlaylistMeta;
  pagination: PlaylistPagination;
}

export const playlistsApi = {
  /**
   * Complete, deduplicated playlist placements for one song or artist on one platform.
   *
   * `scope` separates the two views: "song" for a pitch's analytics page, "artist" for the roster
   * and artist analytics views, where one playlist can legitimately carry several of the artist's
   * songs.
   */
  getPlaylists: (
    scope: "song" | "artist",
    uuid: string,
    platform: string,
    {
      type = "editorial",
      currentOnly = 1,
      countryCode,
      search,
      page = 1,
      limit = 10,
      hideGeoVariants = false,
      playlistFamily,
      bustBrowserCache = false,
    }: PlaylistQuery = {}
  ) =>
    api.get(`/playlists/${scope}/${uuid}/${platform}`, {
      params: {
        type,
        currentOnly,
        // An empty string would be sent as `countryCode=`; undefined drops the param entirely.
        countryCode: countryCode || undefined,
        search: search?.trim() || undefined,
        page,
        limit,
        hideGeoVariants: hideGeoVariants ? 1 : undefined,
        playlistFamily: playlistFamily || undefined,
        _t: bustBrowserCache ? Date.now() : undefined,
      },
      // A cold fetch walks every SoundCharts page, which on a large artist is several seconds more
      // than the client's 10s default allows.
      timeout: 60000,
    }),

  /**
   * Syncs one song or artist on one platform from SoundCharts now, and resolves once the new
   * rows are stored — read the playlists again afterwards.
   *
   * Rejects with 429 inside the 5-minute window after the last refresh, and with 409 while
   * another sync runs or while a pitched record's history is still loading. `message` on
   * either response is written for the user.
   */
  refreshPlaylists: (scope: "song" | "artist", uuid: string, platform: string) =>
    // `{}` rather than null: axios sends null as the body "null" with a JSON content type,
    // which express.json() rejects before the route runs.
    api.post(`/playlists/${scope}/${uuid}/${platform}/refresh`, {}, { timeout: 120000 }),
};
